import { createContext, useContext, useMemo, useState, useEffect, type ReactNode } from 'react';
import enUS from './messages/en-US';
import esES from './messages/es-ES';
import frFR from './messages/fr-FR';

export const LOCALES = {
  ENGLISH: 'en' as const,
  SPANISH: 'es' as const,
  FRENCH:  'fr' as const,
};

export type Locale = (typeof LOCALES)[keyof typeof LOCALES];

const MESSAGES: Record<Locale, Record<string, string>> = {
  [LOCALES.ENGLISH]: enUS,
  [LOCALES.SPANISH]: esES,
  [LOCALES.FRENCH]:  frFR,
};

const STORAGE_KEY = 'metha-docs:lang';

function detectInitialLocale(): Locale {
  if (typeof window === 'undefined') return LOCALES.ENGLISH;
  const stored = window.localStorage.getItem(STORAGE_KEY) as Locale | null;
  if (stored && Object.values(LOCALES).includes(stored)) return stored;
  const nav = window.navigator.language.toLowerCase();
  if (nav.startsWith('es')) return LOCALES.SPANISH;
  if (nav.startsWith('fr')) return LOCALES.FRENCH;
  return LOCALES.ENGLISH;
}

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectInitialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => ({
    locale,
    setLocale: (l) => {
      setLocaleState(l);
      try { window.localStorage.setItem(STORAGE_KEY, l); } catch { /* private mode */ }
    },
    t: (key) => MESSAGES[locale][key] ?? MESSAGES[LOCALES.ENGLISH][key] ?? key,
  }), [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

export function useTranslation() {
  return useI18n().t;
}
